import { List, Set } from 'immutable';
import { parse } from 'qs';
import { createSelector } from '@reduxjs/toolkit';
import { records as jobRecords } from '@saucelabs/sl-module-jobs';

import { records as testFrameworkRecords } from './../batch-framework-commands';
import { records } from '../../webdriver-commands';
import {
  AssetMap,
  AssetScreenshotList,
  TestCaseScreenshotList,
  ScreenshotList,
  isStrings,
} from './types';

export const selectJobRecord = (state) => state.apps.job.job.get('job') as jobRecords.JobRecord;

export const selectCommands = (state) => (
  state.apps.job.job.get('commands') as List<records.SeleniumCommand>
);

export const selectActiveCommand = (state): records.SeleniumCommand | undefined => {
  const active = state.apps.job.job.getIn(['commandsState', 'active']) as number;
  const commands = state.apps.job.job.get('commands') as List<records.SeleniumCommand>;

  if (!List.isList(commands)) {
    return undefined;
  }
  return commands.find((val) => val?.get('id') === active);
};

export const selectExpandedCommands = createSelector(
  [
    (store) => store.apps.job.job.getIn(['commandsState', 'expanded']) as Set<number>,
    selectCommands,
  ],
  (expanded, commands) => (
    commands.filter((command) => {
      const id = command?.get('id');
      return expanded.has(id);
    })
  ),
);

export const selectTestCases = createSelector(
  [
    (store: any) => store.apps.job.job.get('commands')?.get('testsuites') as List<testFrameworkRecords.Testsuite> | undefined,
  ],
  (testsuites) => {
    const testcases = testsuites?.flatMap(
      (suite) => (
        suite?.testcases as List<testFrameworkRecords.TestCase>),
    ) ?? List();
    return testcases;
  },
);

/**
 * Selects all screenshots from the store.
 *
 * The structure and location of the screenshots in the store varies depending on the type of job.
 * This selector normalizes the screenshots into a string[].
 */
export const selectScreenshotAssets = createSelector(
  [
    (store: any) => store.apps.job.job.get('assets') as AssetMap,
  ],
  (assets) => {
    const { screenshots } = assets;
    if (Array.isArray(screenshots) && screenshots.length > 0) {
      if (isStrings(screenshots)) {
        return screenshots;
      }
      return screenshots.map((screenshot) => screenshot.url);
    }
    return Object.keys(assets ?? {}).filter((name) => name.endsWith('.png')).sort();
  },
);

export const selectScreenshotList = createSelector(
  [
    selectScreenshotAssets,
  ],
  (screenshots) => new AssetScreenshotList(screenshots),
);

export const selectCommandScreenshots = createSelector(
  [
    selectScreenshotAssets,
    selectJobRecord,
    (store: any) => store.router.location.search as string,
  ],
  (screenshots, jobRecord, locationSearch) => {
    const { auth } = parse(locationSearch, { ignoreQueryPrefix: true });

    const resolvedScreenshots = screenshots.map(
      (screenshot) => jobRecord.getAssetsFullUrl(screenshot, auth),
    );

    return new AssetScreenshotList(resolvedScreenshots);
  },
);

export const selectScreenshotBundleUrl = createSelector(
  [
    (store: any) => store.apps.job.job.get('job') as jobRecords.JobRecord,
    (store: any) => store.apps.job.job.get('assets')?.screenshots as unknown[] | null | undefined,
    (store: any) => store.router.location.search as string,
  ],
  (jobRecord, screenshots, locationSearch) => {
    // NOTE: Screenshot bundles are only available if the screenshots are collected
    // in the assets.screenshots array. There are two(?) cases this will be true:
    // 1. For assets served by resto, if the screenshot names match a known, expected
    // format -- [0-9]{4}screenshot.png
    // 2. Any RDC job
    if ((screenshots?.length ?? 0) === 0) {
      return null;
    }

    const { auth } = parse(locationSearch, { ignoreQueryPrefix: true });
    return jobRecord?.getScreenshotsBundleUrl(auth) ?? null;
  },
);

export const selectScreenshotIndex = (store) => store.apps.job.job.get('screenshotIndex') as number;

export const selectTestCaseScreenshots = createSelector(
  [
    selectScreenshotAssets,
    selectTestCases,
  ],
  (screenshotAssets, testCases): ScreenshotList => {
    let screenshots: ScreenshotList;

    // Does every attachment described by the test-report exist as an asset
    const allAttachments = testCases
      .flatMap((tc) => tc?.attachments as List<testFrameworkRecords.Attachment>)
      .map((attachment) => attachment?.path);
    const testCaseScreenshotsExist = (
      allAttachments.size > 0 &&
      allAttachments.every((name) => !!name && screenshotAssets.includes(name))
    );

    if (testCaseScreenshotsExist) {
      screenshots = new TestCaseScreenshotList(testCases.toArray());
    } else {
      screenshots = new AssetScreenshotList(screenshotAssets);
    }
    return screenshots;
  },
);
